import { IS_BLOCK_EDITOR_V1_ENABLED } from '@/components/constants/flags';
import {
  type EditorVersionResolution,
  resolveEditorVersion,
  useResolvedEditorVersion,
} from '@/hooks/editor-version/useResolvedEditorVersion';
import {
  isAmdMarkdownSectionalEditorEnabled,
  useAmdMarkdownSectionalEditorEnabled,
} from '@/hooks/integrations/useAmdMarkdownSectionalEditorEnabled';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { GET_INTEGRATION_ONBOARDING_SESSIONS } from '@/hooks/query/integrations/general/onboarding-session/useQueryIntegrationOnboardingSessions';
import { type EHRProfileResponse } from '@/types';
import { isTelusMarkdownSectional } from '@/utils/integration/isTelusMarkdownSectional';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { type SearchIntegrationOnboardingSessionsResponse } from '@heidi/integrations/connected-apps/contracts';
import { EHRProvider, OnboardingSessionStatus } from '@heidi/schema/ml-scribe';
import { GET_USER_EHR } from './query/integrations/useQueryUserEHR';

const isBlockEditorV1Enabled = (
  userEhrProvider: EHRProvider | undefined,
  resolution: Pick<EditorVersionResolution, 'version' | 'isAuthoritative'>,
  isAmdMarkdownSectional = false
) => {
  // A concrete backend-resolved editor version decides for every provider,
  // ahead of the vendor carve-outs and the release flag. This boolean drives
  // the generated note's content_type, so it must agree with the editor
  // `applyResolvedEditorVersion` renders: json/markdown render their own
  // sectional editors (and, for Athena, push via the integration-mixin sync
  // endpoints — block editor v1 on would route the push to legacy
  // /ehr/uploader with no payload); html renders block editor v1.
  if (resolution.isAuthoritative) {
    return resolution.version === 'html';
  }
  const isAthena = userEhrProvider === EHRProvider.athenahealth;
  // AdvancedMD's markdown sectional editor renders the vMarkdown editor (SectionNode
  // + Link-EHR dropdown). Block editor v1 must stay off even when the global release
  // flag is on, otherwise the legacy HTML editor wins and the section-link dropdown
  // never mounts.
  const isAdvancedMD = userEhrProvider === EHRProvider.AdvancedMD;
  if (isAdvancedMD && isAmdMarkdownSectional) {
    return false;
  }
  // Telus CHR is markdown sectional for every onboarded user (unflagged: no
  // production cohort on the legacy editors), so the same suppression applies
  // unconditionally.
  if (isTelusMarkdownSectional(userEhrProvider)) {
    return false;
  }
  const enableBlockEditorV1 = IS_BLOCK_EDITOR_V1_ENABLED();
  return isAthena || enableBlockEditorV1;
};

export const useEnableBlockEditorV1 = (ehrProviderFromSource?: EHRProvider | null) => {
  const { integrationEhrProvider: userEhrProvider } =
    useIntegrationEhrInfo(ehrProviderFromSource);
  const resolution = useResolvedEditorVersion(userEhrProvider);
  const isAmdMarkdownSectional = useAmdMarkdownSectionalEditorEnabled(userEhrProvider);

  return isBlockEditorV1Enabled(userEhrProvider, resolution, isAmdMarkdownSectional);
};

/**
 * Imperative sibling of {@link useEnableBlockEditorV1} for non-React call sites
 * (e.g. note generation in `_stream-response.ts`).
 *
 * @param providerOverride - When supplied (e.g. the ACTIVE session's provider),
 *   the doc-type decision binds to it instead of the first-connected onboarding
 *   session. Mirrors the hook's optional `ehrProviderFromSource`, so a
 *   multi-integration user's generation agrees with the editor being shown.
 *   Falls back to the first-connected onboarding session / EHR profile when
 *   omitted or undefined.
 */
export const isEnableBlockEditorV1 = (providerOverride?: EHRProvider) => {
  const onboardingSessionProvider = reactQueryClient
    .getQueryData<SearchIntegrationOnboardingSessionsResponse>([
      GET_INTEGRATION_ONBOARDING_SESSIONS,
      { params: { status: OnboardingSessionStatus.completed } },
    ])
    ?.sessions?.at(0)?.provider as EHRProvider | undefined;

  const ehrProfileProvider = reactQueryClient.getQueryData<EHRProfileResponse>([
    GET_USER_EHR,
  ])?.ehr_profile?.ehr_provider;

  const provider = providerOverride ?? onboardingSessionProvider ?? ehrProfileProvider;
  return isBlockEditorV1Enabled(
    provider,
    resolveEditorVersion(provider),
    isAmdMarkdownSectionalEditorEnabled(provider)
  );
};
